import { tr } from "date-fns/locale/tr";
import { default as React, useEffect, useReducer, useState } from "react";
import { Button, InputGroup, Tooltip } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import "../datepicker.css";
import useAxios from "../hooks/useAxios";
import "../SummaryScreen.css";
import {
  getError,
  getWeekNumber,
  turkishCurrencyFormatterNoPrecision,
} from "../utils";

const CustomYAxisTickFormatter = (value) => {
  return `${turkishCurrencyFormatterNoPrecision(value)}`; // Format as currency with commas
};

registerLocale("tr", tr);

const isSameDate = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const typeAndDifferentDates = [
  {
    type: "daily",
    dates: [
      { name: "7 Gün", value: 7 },
      { name: "30 Gün", value: 31 },
      { name: "365 Gün", value: 366 },
      { name: "Tümü", value: 1000000 },
    ],
  },
  {
    type: "weekly",
    dates: [
      { name: "4 Hafta", value: 4 },
      { name: "8 Hafta", value: 8 },
      { name: "12 Hafta", value: 12 },
      { name: "Tümü", value: 1000000 },
    ],
  },
  {
    type: "monthly",
    dates: [
      { name: "6 Ay", value: 6 },
      { name: "12 Ay", value: 12 },
      { name: "36 Ay", value: 36 },
      { name: "Tümü", value: 1000000 },
    ],
  },
  {
    type: "annual",
    dates: [
      { name: "3 Yıl", value: 3 },
      { name: "5 Yıl", value: 5 },
      { name: "10 Yıl", value: 10 },
      { name: "Tümü", value: 1000000 },
    ],
  },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return {
        ...state,
        summary: action.payload,
        loading: false,
      };
    case "FETCH_FAIL":
      return { ...state, loading: false };
    default:
      return state;
  }
};

export default function SummaryScreen() {
  const navigate = useNavigate();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const [type, setType] = useState("daily");

  const [reportDate, setReportDate] = useState(null);
  const [itemId, setItemId] = useState();
  const [timeRange, setTimeRange] = useState(7);
  const axios = useAxios();
  const [{ loading, error, summary }, dispatch] = useReducer(reducer, {
    loading: true,
    error: "",
    summary: { data: [] },
  });

  const fetchSummary = async () => {
    try {
      dispatch({ type: "FETCH_REQUEST" });
      const { data } = reportDate
        ? await axios.get(
            `/api/orders/volumesummary?type=${type}&timeRange=${timeRange}&reportDate=${reportDate.toISOString()}`
          )
        : await axios.get(
            `/api/orders/volumesummary?type=${type}&timeRange=${timeRange}`
          );

      dispatch({ type: "FETCH_SUCCESS", payload: data.summary });
    } catch (err) {
      dispatch({ type: "FETCH_FAIL" });
      toast(getError(err));
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [type, timeRange, reportDate]);

  return (
    <div>
      <Helmet>
        <title>Özet</title>
      </Helmet>

      <div style={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
        <div>
          <InputGroup size={isTabletOrMobile ? "md" : "lg"} className="mb-3">
            <Button
              variant={type === "daily" ? "primary" : "outline-secondary"}
              onClick={() => {
                setType("daily");
                setTimeRange(7);
                setReportDate(null);
              }}
            >
              Günlük
            </Button>

            <Button
              variant={type === "weekly" ? "primary" : "outline-secondary"}
              onClick={() => {
                setType("weekly");
                setTimeRange(4);
                setReportDate(null);
              }}
            >
              Haftalık
            </Button>

            <Button
              variant={type === "monthly" ? "primary" : "outline-secondary"}
              onClick={() => {
                setType("monthly");
                setTimeRange(6);
                setReportDate(null);
              }}
            >
              Aylik
            </Button>

            <Button
              variant={type === "annual" ? "primary" : "outline-secondary"}
              onClick={() => {
                setType("annual");
                setTimeRange(3);
                setReportDate(null);
              }}
            >
              Yıllık
            </Button>
          </InputGroup>
        </div>

        <div>
          <InputGroup
            size={isTabletOrMobile ? "md" : "lg"}
            className="mb-3"
            style={isTabletOrMobile ? {} : { marginLeft: "20px" }}
          >
            {typeAndDifferentDates
              .filter((t) => t.type === type)[0]
              .dates.map((t) => (
                <Button
                  key={t.name + t.value}
                  variant={
                    timeRange === t.value ? "primary" : "outline-secondary"
                  }
                  onClick={() => {
                    setTimeRange(t.value);
                    setReportDate(null);
                  }}
                >
                  {t.name}
                </Button>
              ))}
          </InputGroup>
        </div>
      </div>

      <div className="mb-3" style={{ display: "flex", flexDirection: "row" }}>
        <div className="mt-3">
          <DatePicker
            locale="tr"
            selected={reportDate}
            onChange={(date) => setReportDate(date)}
            dateFormat="dd/MM/yyyy"
            inline
            showMonthYearPicker={type === "monthly"}
            showYearPicker={type === "annual"}
            filterDate={(date) =>
              type === "weekly" ? date.getDay() === 1 : true
            }
            dayClassName={(date) =>
              date.getDay() !== 1 && type === "weekly" ? "fade-day" : ""
            }
          />
        </div>
        {!isTabletOrMobile && !reportDate && (
          <div
            className="mb-3 mt-3"
            style={{
              width: "80%",
              height: "250px",
            }}
          >
            <ResponsiveContainer
              width={isTabletOrMobile || timeRange > 7 ? "100%" : "60%"}
              height="100%"
            >
              <LineChart
                width={500}
                height={300}
                data={summary.data
                  .slice()
                  .filter((item) => item.totalPos + item.totalCache > 10)
                  .reverse()
                  .map((item) => ({
                    name:
                      type == "annual"
                        ? new Date(item.startedAt).toLocaleDateString("tr-TR", {
                            year: "numeric",
                          })
                        : type === "monthly"
                        ? new Date(item.startedAt).toLocaleDateString("tr-TR", {
                            year: "numeric",
                            month: "long",
                          })
                        : type === "weekly"
                        ? `${getWeekNumber(new Date(item.startedAt))}.Hafta`
                        : new Date(item.startedAt).toLocaleDateString("tr-TR", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }),
                    Toplam: item.totalPos + item.totalCache,
                    Harcama: item.totalExpenseCache + item.totalExpensePos,
                    Net:
                      item.totalPos +
                      item.totalCache -
                      item.totalExpenseCache -
                      item.totalExpensePos,
                  }))}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={CustomYAxisTickFormatter} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  strokeWidth={2}
                  dataKey="Toplam"
                  stroke="blue"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div>
        {loading ? (
          <LoadingBox />
        ) : summary.data.length > 0 ? (
          <div className="summary-grid">
            {summary.data.map((item) => (
              <div
                key={item.startedAt + "" + item.totalCache}
                className="summary-card"
                onMouseOver={() => setItemId(item.startedAt)}
                onMouseLeave={() => setItemId(null)}
                onClick={() => {
                  const reportDateParam = item.startedAt;
                  navigate(
                    `/periodicreport?reportDate=${reportDateParam}&type=${type}`
                  );
                }}
              >
                <div className="summary-card-body">
                  <h5 className="summary-card-title text-dark">
                    {type === "annual"
                      ? new Date(item.startedAt).toLocaleDateString("tr-TR", {
                          year: "numeric",
                        })
                      : type === "monthly"
                      ? new Date(item.startedAt).toLocaleDateString("tr-TR", {
                          year: "numeric",
                          month: "long",
                        })
                      : type === "weekly"
                      ? `${getWeekNumber(
                          new Date(item.startedAt)
                        )}.Hafta ${new Date(item.startedAt).getFullYear()}`
                      : new Date(item.startedAt).toLocaleDateString("tr-TR", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                  </h5>

                  <div className="summary-card-text">
                    <div className="summary-card-text-item">
                      <span className="small-font">Toplam</span>
                      <span className="text-end text-bold">
                        <h5>
                          {turkishCurrencyFormatterNoPrecision(
                            item.totalCache + item.totalPos
                          )}
                        </h5>
                      </span>
                    </div>
                    <div className="summary-card-text-item">
                      <span className="small-font">Harcama</span>
                      <span className="text-end">
                        {turkishCurrencyFormatterNoPrecision(
                          item.totalExpensePos + item.totalExpenseCache
                        )}
                      </span>
                    </div>
                    <div className="summary-card-text-item">
                      <span className="small-font">Net</span>
                      <span className="text-end text-dark">
                        {turkishCurrencyFormatterNoPrecision(
                          item.totalCache +
                            item.totalPos -
                            item.totalExpensePos -
                            item.totalExpenseCache
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <MessageBox variant="info">
            Bu tarihe ait rapor yok! Başka bir tarih deneyiniz!
          </MessageBox>
        )}
      </div>
    </div>
  );
}
